// Library Methods
import { useCallback, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { HubConnection } from "@microsoft/signalr";

// Components
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Loader from "src/components/shared/loaders/Loader/Loader";
import blackout from "src/assets/images/live-update/blackLive.jpeg";
import { CrosshairIcon } from "src/assets/icons";

// Contexts
import { useLiveUpdateContext } from "src/contexts/live-update/LiveUpdateContext";
import { useClinicContext } from "src/contexts/clinic/ClinicProvider";

// Hooks
import useTokenAccess from "src/hooks/auth/useTokenAccess";
import useLiveView from "src/hooks/connection/useLiveView";

// Services
import { deviceCalibrateLobby } from "src/services/signalR/api";

// Styles
import { ModalCustomConfirm } from "src/styles/mui/muiStylesHelper";

type LiveUpdateEyeStreamType = {
  connection: HubConnection | null;
};

const LiveUpdateEyeStream = ({ connection }: LiveUpdateEyeStreamType) => {
  const { t } = useTranslation();
  const { getToken } = useTokenAccess();

  const { clinicInfo } = useClinicContext();
  const { device, isNotStarted, isOnline } = useLiveUpdateContext();

  const canvasRef = useRef<HTMLCanvasElement>(null);

  const { connectionState, error } = useLiveView({
    clinicId: clinicInfo?.id,
    deviceId: device?.id ?? null,
    canvasRef,
  });

  const deviceStageType = device?.deviceStatus?.stage?.stageType ?? "";
  const hmdInProximity = Boolean(device?.deviceStatus?.hmdInProximity);

  const inExternalCalibration = useMemo(
    () => deviceStageType === "ExternalCalibration",
    [deviceStageType]
  );

  const streamConnected = connectionState === "open";

  const displayBlackStream = useMemo(
    () => inExternalCalibration || !hmdInProximity || !streamConnected,
    [inExternalCalibration, hmdInProximity, streamConnected]
  );

  // Recenter patient view request
  const handleDeviceCalibration = useCallback(async () => {
    if (!device?.id || !connection) return;

    try {
      const token = await getToken();
      await deviceCalibrateLobby(token, device.id, connection);
    } catch (e) {
      console.error(e);
    }
  }, [connection, device?.id, getToken]);

  // The socket reconnects on its own after a drop (see useLiveView), so
  // "connecting" here covers both the very first connection attempt and any
  // later reconnect -- there's no dead-end error state to show; a reconnect
  // in progress is instead surfaced through the overlay text below.
  if (connectionState === "idle") {
    return <Loader containerProps={{ height: "100%" }} />;
  }

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: "500px",
        position: "relative",
        aspectRatio: "2 / 1",
      }}
    >
      <canvas
        ref={canvasRef}
        style={{
          width: "100%",
          height: "100%",
          objectFit: "cover",
          borderRadius: "4px",
          display: displayBlackStream ? "none" : "block",
        }}
      />
      {displayBlackStream && (
        <img
          src={blackout}
          alt="stream"
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            borderRadius: "4px",
          }}
        />
      )}
      {!displayBlackStream && (
        <Button
          startIcon={<CrosshairIcon height="20px" width="20px" />}
          sx={{
            position: "absolute",
            bottom: "8px",
            right: "8px",
            textTransform: "none",

            ...ModalCustomConfirm(),

            fontSize: "12px",
          }}
          disabled={isNotStarted || !isOnline}
          onClick={handleDeviceCalibration}
        >
          {t("Recenter")}
        </Button>
      )}
      {displayBlackStream && (
        <Box
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
          }}
        >
          <Typography
            sx={{
              color: "#FFF",
              fontWeight: 600,
              fontSize: "16px",
              textAlign: "center",
            }}
          >
            {!hmdInProximity
              ? t("The device has been removed.")
              : inExternalCalibration
                ? t("Calibration in progress.")
                : !streamConnected
                  ? error ?? t("Connecting to the live stream...")
                  : ""}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default LiveUpdateEyeStream;
